import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { GameDefinition } from "@spieleparadies/shared";

import { getSocket } from "../lib/socket";
import { SERVER_URL } from "../lib/env";
import { usePlayerStore } from "../state/usePlayerStore";
import { useRoomStore } from "../state/useRoomStore";
import { useGameCatalogStore } from "../state/useGameCatalogStore";

type DialogMode =
  | { kind: "create"; gameId: string }
  | { kind: "join" }
  | { kind: "closed" };

export function HomePage() {
  const navigate = useNavigate();
  const playerName = usePlayerStore((s) => s.name);
  const setPlayerName = usePlayerStore((s) => s.setName);
  const setRoom = useRoomStore((s) => s.setRoom);

  const setCatalog = useGameCatalogStore((s) => s.setGames);
  const [games, setGames] = useState<GameDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogMode>({ kind: "closed" });
  const [nameInput, setNameInput] = useState(playerName);
  const [codeInput, setCodeInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/games`);
        const data = (await res.json()) as GameDefinition[];
        if (!cancelled) {
          setGames(data);
          setCatalog(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Konnte den Spiele-Server nicht erreichen. Läuft `npm run dev:server`?");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setCatalog]);

  const openCreateDialog = (game: GameDefinition) => {
    if (!game.available) return;
    setDialog({ kind: "create", gameId: game.id });
    setNameInput(playerName);
    setError(null);
  };

  const openJoinDialog = () => {
    setDialog({ kind: "join" });
    setNameInput(playerName);
    setCodeInput("");
    setError(null);
  };

  const submitCreate = async () => {
    if (dialog.kind !== "create") return;
    const name = nameInput.trim();
    if (!name) {
      setError("Bitte gib einen Spielernamen ein.");
      return;
    }
    setBusy(true);
    setError(null);
    setPlayerName(name);
    getSocket().emit(
      "room:create",
      { gameId: dialog.gameId, playerName: name },
      (res) => {
        setBusy(false);
        if (!res.ok) {
          setError(res.error);
          return;
        }
        setRoom(res.room);
        setDialog({ kind: "closed" });
        navigate(`/lobby/${res.room.code}`);
      },
    );
  };

  const submitJoin = async () => {
    const name = nameInput.trim();
    const code = codeInput.trim().toUpperCase();
    if (!name || !code) {
      setError("Spielername und Raum-Code sind erforderlich.");
      return;
    }
    setBusy(true);
    setError(null);
    setPlayerName(name);
    getSocket().emit("room:join", { code, playerName: name }, (res) => {
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setRoom(res.room);
      setDialog({ kind: "closed" });
      navigate(`/lobby/${res.room.code}`);
    });
  };

  return (
    <>
      <section className="hero">
        <h1>Willkommen im Spieleparadies</h1>
        <p>
          Wähle ein Spiel, eröffne einen Raum und lade deine Freunde mit einem Code ein.
        </p>
        <div className="row" style={{ maxWidth: 360, margin: "24px auto 0" }}>
          <button className="btn btn-primary" onClick={openJoinDialog}>
            Raum beitreten
          </button>
        </div>
      </section>

      <div className="section-title">
        <h2>Spiele</h2>
      </div>

      {loading && <p style={{ color: "var(--text-muted)" }}>Lädt Spiele …</p>}
      {error && dialog.kind === "closed" && (
        <div className="card" style={{ borderColor: "var(--danger)" }}>
          {error}
        </div>
      )}

      <div className="games-grid">
        {games.map((game) => (
          <article
            key={game.id}
            className={`card game-card ${game.available ? "" : "unavailable"}`}
            onClick={() => openCreateDialog(game)}
          >
            <div className="thumb">
              <img src={game.thumbnail} alt={game.title} onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }} />
            </div>
            <h3>{game.title}</h3>
            <p>{game.description}</p>
            <div className="tags">
              {game.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
              <span className="tag">
                {game.minPlayers === game.maxPlayers
                  ? `${game.maxPlayers} Spieler`
                  : `${game.minPlayers}–${game.maxPlayers} Spieler`}
              </span>
              {!game.available && <span className="tag">Bald verfügbar</span>}
            </div>
          </article>
        ))}
      </div>

      {dialog.kind !== "closed" && (
        <div className="dialog" onClick={(e) => e.target === e.currentTarget && setDialog({ kind: "closed" })}>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>
              {dialog.kind === "create" ? "Neuen Raum eröffnen" : "Raum beitreten"}
            </h2>
            <label className="label">Spielername</label>
            <input
              className="input"
              value={nameInput}
              maxLength={20}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="z. B. Sam"
            />
            {dialog.kind === "join" && (
              <>
                <label className="label" style={{ marginTop: 16 }}>Raum-Code</label>
                <input
                  className="input"
                  value={codeInput}
                  maxLength={6}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="z. B. ABC23"
                  style={{ letterSpacing: "0.2em", textTransform: "uppercase" }}
                />
              </>
            )}
            {error && (
              <p style={{ color: "var(--danger)", marginTop: 12 }}>{error}</p>
            )}
            <div className="row" style={{ marginTop: 20 }}>
              <button
                className="btn btn-ghost"
                onClick={() => setDialog({ kind: "closed" })}
                disabled={busy}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={dialog.kind === "create" ? submitCreate : submitJoin}
                disabled={busy}
              >
                {dialog.kind === "create" ? "Raum erstellen" : "Beitreten"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
